<template>
  <div :class="{ 'page-root': !embedded }">
    <!-- embedded(방송하기 페이지)에선 타이틀/설명을 호스트가 카드 밖에 렌더 → 여기선 standalone만 -->
    <template v-if="!embedded">
      <h1>
        <span class="font-weight-bold" style="font-family: maplestory"> {{ $t("broadcastManage.title") }} </span>
      </h1>
      <p class="text-body-2 grey--text mt-1 mb-3">{{ $t("broadcastManage.subtitle") }}</p>
    </template>

    <v-tabs v-model="tab" class="mt-2" color="primary lighten-1">
      <v-tab>
        <v-chzzk-icon :size="18" class="mr-1" style="border-radius: 4px" contain></v-chzzk-icon>
        {{ $t("broadcastManage.tabChzzk") }}
        <v-icon v-if="isConnected('chzzk')" size="14" color="success" class="ml-1">mdi-check-circle</v-icon>
      </v-tab>
      <v-tab>
        <v-twitch-icon :size="18" class="mr-1" style="border-radius: 4px" contain></v-twitch-icon>
        {{ $t("broadcastManage.tabTwitch") }}
        <v-icon v-if="isConnected('twitch')" size="14" color="success" class="ml-1">mdi-check-circle</v-icon>
      </v-tab>
      <v-tab>
        <v-afreeca-icon :size="22" class="mr-1" style="border-radius: 4px" contain></v-afreeca-icon>
        {{ $t("broadcastManage.tabSoop") }}
      </v-tab>
      <v-tab>
        <v-icon :size="20" class="mr-1" color="red">mdi-youtube</v-icon>
        {{ $t("broadcastManage.tabYoutube") }}
      </v-tab>
      <v-tab>
        <v-icon :size="18" class="mr-1" color="pink">mdi-instagram</v-icon>
        {{ $t("broadcastManage.tabInstagram") }}
      </v-tab>
      <!-- 관리자 전용: 치지직(릴레이) — 남의 채널 채팅을 relay로 읽기. 아이콘은 /on-air 상태바와 동일(전파탑) -->
      <v-tab v-if="isLocalEnv">
        <v-icon :size="18" class="mr-1" color="deep-purple">mdi-radio-tower</v-icon>
        {{ $t("broadcastManage.tabChzzkRelay") }}
        <v-icon v-if="isConnected('chzzk_relay')" size="14" color="success" class="ml-1">mdi-check-circle</v-icon>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- 치지직 -->
      <v-tab-item>
        <platform-channel-card
          :title="$t('broadcastManage.chzzkTitle')"
          :desc="$t('broadcastManage.chzzkDesc')"
          :connected="isConnected('chzzk')"
          show-toggle
          :enabled="enabled.chzzk"
          :enabled-busy="enabledBusy.chzzk"
          @toggle="onToggleEnabled('chzzk', $event)"
        >
          <!-- OAuth형: 연동 시에만 입력(비활성, 채널명 표시 + 해제). 미연동 시엔 박스 자체 숨김 → hero 버튼만 -->
          <template v-if="isConnected('chzzk')" #input>
            <v-text-field
              :value="channelLabel('chzzk')"
              outlined
              dense
              disabled
              hide-details="auto"
              class="mb-3 pcc-input"
            >
              <template #append>
                <v-btn
                  text
                  small
                  color="error"
                  class="pcc-input__btn"
                  :loading="isDisconnectingChzzk"
                  @click="onDisconnectChzzk"
                >
                  {{ $t("broadcastManage.disconnectShort") }}
                </v-btn>
              </template>
            </v-text-field>
          </template>
          <!-- OAuth hero 버튼: 미연동 시 강조 (w-100) -->
          <template v-if="!isConnected('chzzk')" #actions>
            <v-btn block color="primary" :loading="isConnectingChzzk" @click="onConnectChzzk">
              <v-chzzk-icon :size="16" class="mr-2" style="border-radius: 3px" contain></v-chzzk-icon>
              {{ $t("broadcastManage.chzzkConnect") }}
            </v-btn>
          </template>
        </platform-channel-card>
      </v-tab-item>

      <!-- 트위치 -->
      <v-tab-item>
        <platform-channel-card
          :title="$t('broadcastManage.twitchTitle')"
          :desc="$t('broadcastManage.twitchDesc')"
          :connected="isConnected('twitch')"
          show-toggle
          :enabled="enabled.twitch"
          :enabled-busy="enabledBusy.twitch"
          @toggle="onToggleEnabled('twitch', $event)"
        >
          <template #input>
            <!-- 검색형: 연동 시 입력 잠금. 변경하려면 해제 후 재연동 -->
            <v-text-field
              v-model="twitchChannel"
              outlined
              dense
              :disabled="isConnected('twitch')"
              hide-details="auto"
              :placeholder="$t('broadcastManage.twitchPlaceholder')"
              class="mb-3 pcc-input"
              @keyup.enter="onConnectTwitch"
            >
              <template #append>
                <v-btn
                  text
                  small
                  :color="isConnected('twitch') ? 'error' : 'primary'"
                  class="pcc-input__btn"
                  :loading="isConnected('twitch') ? isDisconnectingTwitch : isConnectingTwitch"
                  :disabled="!isConnected('twitch') && !twitchChannel"
                  @click="isConnected('twitch') ? onDisconnectTwitch() : onConnectTwitch()"
                >
                  {{
                    isConnected("twitch") ? $t("broadcastManage.disconnectShort") : $t("broadcastManage.connectShort")
                  }}
                </v-btn>
              </template>
            </v-text-field>
          </template>
        </platform-channel-card>
      </v-tab-item>

      <!-- 숲(SOOP) -->
      <v-tab-item>
        <platform-channel-card :title="$t('broadcastManage.soopTitle')" coming-soon></platform-channel-card>
      </v-tab-item>

      <!-- 유튜브 -->
      <v-tab-item>
        <platform-channel-card :title="$t('broadcastManage.youtubeTitle')" coming-soon></platform-channel-card>
      </v-tab-item>

      <!-- 인스타 라이브 -->
      <v-tab-item>
        <platform-channel-card :title="$t('broadcastManage.instagramTitle')" coming-soon></platform-channel-card>
      </v-tab-item>

      <!-- 관리자 전용: 치지직(릴레이) — 대상 채널명 입력 → relay가 시청자로 입장해 채팅 읽기 -->
      <v-tab-item v-if="isLocalEnv">
        <platform-channel-card
          :title="$t('broadcastManage.relayTitle')"
          :desc="$t('broadcastManage.relayDesc')"
          :connected="isConnected('chzzk_relay')"
        >
          <template #input>
            <!-- 검색형: 연동 시 입력 잠금. 변경하려면 해제 후 재연동 -->
            <v-text-field
              v-model="relayChannel"
              outlined
              dense
              :disabled="isConnected('chzzk_relay')"
              hide-details="auto"
              :placeholder="$t('broadcastManage.relayPlaceholder')"
              class="mb-3 pcc-input"
              @keyup.enter="onConnectRelay"
            >
              <template #append>
                <v-btn
                  text
                  small
                  :color="isConnected('chzzk_relay') ? 'error' : 'deep-purple'"
                  class="pcc-input__btn"
                  :loading="isConnected('chzzk_relay') ? isDisconnectingRelay : isConnectingRelay"
                  :disabled="!isConnected('chzzk_relay') && !relayChannel"
                  @click="isConnected('chzzk_relay') ? onDisconnectRelay() : onConnectRelay()"
                >
                  {{
                    isConnected("chzzk_relay")
                      ? $t("broadcastManage.disconnectShort")
                      : $t("broadcastManage.connectShort")
                  }}
                </v-btn>
              </template>
            </v-text-field>
          </template>
        </platform-channel-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import SupabaseApi from "@/api/SupabaseApi";
import ChzzkOAuthApi from "@/api/ChzzkOAuthApi";
import ChzzkRelayApi from "@/api/ChzzkRelayApi";
import TwitchApi from "@/api/TwitchApi";
import { isLocal } from "@/utils/env-utils";
import VChzzkIcon from "@/components/icons/VChzzkIcon.vue";
import VAfreecaIcon from "@/components/icons/VAfreecaIcon.vue";
import VTwitchIcon from "@/components/icons/VTwitchIcon.vue";
import PlatformChannelCard from "./components/PlatformChannelCard.vue";

export default {
  components: { VChzzkIcon, VAfreecaIcon, VTwitchIcon, PlatformChannelCard },
  // embedded: 다른 페이지(방송하기)에 편입될 때 자체 page-root(패딩/전체높이/배경)를 끔
  props: { embedded: { type: Boolean, default: false } },
  computed: {
    // 치지직(릴레이) 탭 활성조건 — 로컬 환경에서만 노출 (구 admin 게이트에서 변경)
    isLocalEnv() {
      return isLocal();
    },
  },
  created() {
    this.handleChzzkCallback();
    this.loadPlatforms();
  },
  data() {
    return {
      tab: 0,
      // 플랫폼 연동 (platform_oauth) + 활성 토글 (user_platform_enabled)
      connections: {}, // { chzzk: {channel_name, is_connected,...}, twitch: {...} }
      enabled: { chzzk: false, twitch: false, youtube: false, soop: false },
      enabledBusy: { chzzk: false, twitch: false, youtube: false, soop: false },
      twitchChannel: "",
      relayChannel: "", // 관리자 relay 대상 채널명 (chzzk_relay)
      isConnectingChzzk: false,
      isDisconnectingChzzk: false,
      isConnectingTwitch: false,
      isDisconnectingTwitch: false,
      isConnectingRelay: false,
      isDisconnectingRelay: false,
    };
  },
  methods: {
    // 치지직 OAuth 콜백 복귀 (Edge가 /broadcast/setting?tab=chzzk&chzzk=connected 로 302)
    handleChzzkCallback() {
      const { tab, chzzk } = this.$route.query || {};
      if (tab === "chzzk") this.tab = 0; // 치지직 탭
      if (!chzzk) return;

      if (chzzk === "connected") {
        this.$notify({ type: "success", title: this.$t("broadcastManage.callbackConnected"), duration: 2400 });
      } else if (chzzk === "expired") {
        this.$notify({ type: "warning", title: this.$t("broadcastManage.callbackExpired"), duration: 2800 });
      } else {
        this.$notify({ type: "error", title: this.$t("broadcastManage.callbackError"), duration: 2800 });
      }
      this.$router.replace({ query: {} }).catch(() => {});
    },
    // 플랫폼 연동 상태 + 활성 토글 로드 (platform_oauth / user_platform_enabled)
    async loadPlatforms() {
      try {
        const [connections, enabled] = await Promise.all([
          SupabaseApi.PlatformOAuth.fetchConnections(),
          SupabaseApi.UserPlatformEnabled.fetch(),
        ]);
        this.connections = connections;
        this.enabled = { chzzk: false, twitch: false, youtube: false, soop: false, ...enabled };
        const tw = connections.twitch;
        if (tw && tw.channel_name) this.twitchChannel = tw.channel_name;
        const rel = connections.chzzk_relay;
        if (rel && rel.channel_name) this.relayChannel = rel.channel_name;
      } catch (error) {
        console.error(error);
      }
    },
    isConnected(provider) {
      const c = this.connections[provider];
      return !!(c && c.is_connected);
    },
    channelLabel(provider) {
      const c = this.connections[provider] || {};
      return c.channel_name || c.channel_id || this.$t("broadcastManage.channelChecking");
    },
    // 활성 토글 (직접 RLS RW). 실패 시 서버 상태로 되돌림.
    async onToggleEnabled(provider, value) {
      this.$set(this.enabled, provider, value); // 낙관적 반영 (스위치가 :input-value 라 직접 갱신)
      this.$set(this.enabledBusy, provider, true);
      try {
        await SupabaseApi.UserPlatformEnabled.setEnabled(provider, value);
      } catch (error) {
        console.error(error);
        this.$set(this.enabled, provider, !value); // 롤백
        this.$notify({ type: "error", title: this.$t("broadcastManage.settingChangeFailed"), duration: 2400 });
      } finally {
        this.$set(this.enabledBusy, provider, false);
      }
    },
    async onConnectChzzk() {
      this.isConnectingChzzk = true;
      try {
        await ChzzkOAuthApi.start(); // 동의 URL로 페이지 이동
      } catch (error) {
        console.error(error);
        this.$notify({ type: "error", title: this.$t("broadcastManage.chzzkConnectFailed"), duration: 2400 });
        this.isConnectingChzzk = false;
      }
    },
    async onDisconnectChzzk() {
      if (!confirm(this.$t("broadcastManage.confirmDisconnectChzzk"))) return;
      this.isDisconnectingChzzk = true;
      try {
        await ChzzkOAuthApi.disconnect();
        await this.loadPlatforms();
        this.$notify({ type: "success", title: this.$t("broadcastManage.chzzkDisconnected"), duration: 2000 });
      } catch (error) {
        console.error(error);
        this.$notify({ type: "error", title: this.$t("broadcastManage.disconnectFailed"), duration: 2400 });
      } finally {
        this.isDisconnectingChzzk = false;
      }
    },
    async onConnectTwitch() {
      const channel = (this.twitchChannel || "").trim();
      if (!channel) return;
      this.isConnectingTwitch = true;
      try {
        await TwitchApi.connect(channel);
        await this.loadPlatforms();
        this.$notify({ type: "success", title: this.$t("broadcastManage.twitchConnected"), duration: 2000 });
      } catch (error) {
        console.error(error);
        this.$notify({ type: "error", title: this.$t("broadcastManage.twitchConnectFailed"), duration: 2400 });
      } finally {
        this.isConnectingTwitch = false;
      }
    },
    async onDisconnectTwitch() {
      if (!confirm(this.$t("broadcastManage.confirmDisconnectTwitch"))) return;
      this.isDisconnectingTwitch = true;
      try {
        await TwitchApi.disconnect();
        this.twitchChannel = "";
        await this.loadPlatforms();
        this.$notify({ type: "success", title: this.$t("broadcastManage.twitchDisconnected"), duration: 2000 });
      } catch (error) {
        console.error(error);
        this.$notify({ type: "error", title: this.$t("broadcastManage.disconnectFailed"), duration: 2400 });
      } finally {
        this.isDisconnectingTwitch = false;
      }
    },
    // 관리자 relay: 대상 채널명 저장 (platform_oauth provider='chzzk_relay'). 오버레이가 identity로 받아 연결.
    async onConnectRelay() {
      const channel = (this.relayChannel || "").trim();
      if (!channel) return;
      this.isConnectingRelay = true;
      try {
        const res = await ChzzkRelayApi.connect(channel);
        const ch = res && res.channel;
        if (ch && ch.channelName) this.relayChannel = ch.channelName; // 검색으로 확정된 정식 이름
        await this.loadPlatforms();
        const suffix = ch ? ` (${ch.channelName}${ch.openLive ? " 🔴LIVE" : ""})` : "";
        this.$notify({ type: "success", title: this.$t("broadcastManage.relayConnected") + suffix, duration: 2600 });
      } catch (error) {
        console.error(error);
        const notFound = error && error.message === "E_CHANNEL_NOT_FOUND";
        const key = notFound ? "broadcastManage.relayNotFound" : "broadcastManage.relayConnectFailed";
        this.$notify({ type: "error", title: this.$t(key), duration: 2600 });
      } finally {
        this.isConnectingRelay = false;
      }
    },
    async onDisconnectRelay() {
      if (!confirm(this.$t("broadcastManage.confirmDisconnectRelay"))) return;
      this.isDisconnectingRelay = true;
      try {
        await ChzzkRelayApi.disconnect();
        this.relayChannel = "";
        await this.loadPlatforms();
        this.$notify({ type: "success", title: this.$t("broadcastManage.relayDisconnected"), duration: 2000 });
      } catch (error) {
        console.error(error);
        this.$notify({ type: "error", title: this.$t("broadcastManage.disconnectFailed"), duration: 2400 });
      } finally {
        this.isDisconnectingRelay = false;
      }
    },
  },
};
</script>

<style scoped>
/* input 우측 안쪽에 연동 버튼을 수직 중앙 정렬 + 오른쪽 끝으로 당김.
   Vuetify가 outlined+dense일 때 append-inner에 margin-top(8px)+align-self:flex-start를
   높은 specificity로 걸기 때문에 !important로 덮어 수직 중앙 정렬. */
.pcc-input >>> .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}
.pcc-input >>> .v-input__append-inner .pcc-input__btn {
  margin-right: -8px;
}
</style>
